/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Divider, Empty, Form, Row, Select, Spin, Switch, TimePicker, Typography, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Prompt from '../UI/Prompt';
import LivechatSystemMessagesEditor from './LiveChatSystemMessagesEditor';
import BotLanguageSelect from '../UI/BotLanguageSelect';
import BotAvatar from '../UI/BotAvatar';
import CustomInputNumber from './Fields/CustomInputNumber';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import AgentMasking from './Fields/AgentMasking';

// Misc imports
import { WEEKDAYS } from '../../constants/main';
import { ADDITIONAL_ROLES_ORG, ADDITIONAL_ROLES_TENANT, PERMISSIONS_GROUP_ADMIN } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchBotsQuery, useUpdateBotMutation } from '../../store/bots/botsApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import useTheme from '../../utils/hooks/useTheme';
import is from '../../utils/is';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import timeToMinutesAfterMidnight from '../../utils/date/timeToMinutesAfterMidnight';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, PLUGIN_CATEGORY } from '../../constants/integrations';
import { AGENT_MASKING_SETTINGS_CUSTOM_KEY, AGENT_MASKING_SETTINGS_MODES } from './Fields/AgentMasking';
import { useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';

// Constant declarations
const { Option } = Select;
const LIVECHAT_PERMISSIONS = [...PERMISSIONS_GROUP_ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN, ADDITIONAL_ROLES_TENANT.LIVECHAT_ADMIN];

const LiveChatBotSettingsForm = ({
  form,
  onSuccess,
  onCanSubmitChange,
  onLoadingChange,
  enableAgentMaskingSettings=true,
  integrationType,
  showIntegrationSelect = false
}) => {
  const [lcMessagesForm] = Form.useForm();
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { changeBotLanguage } = useBotLanguage(tenantId);
  const { formProps } = useTheme();
  const userPermissions = useUserPermissions(orgId, tenantId);

  const { data: fetchedBot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  // If we are in the bot settings screen, we don't need to fetch the bots (as we already have the bot)
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includePlugins: true } }, { skip: !orgId || tenantId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const { data: integrationsData, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery(
    { orgId, urlParams: { type: integrationType } },
    { skip: !orgId || !showIntegrationSelect }
  );

  const integrationOptions = useMemo(() => {
    return (integrationsData?.items?.map(int => ({ label: int.name, value: int.id }))) ?? [];
  }, [integrationsData]);

  const [, updateState] = useState(); // Hook to force a component re-render

  const bot = useMemo(() => fetchedBot || (bots?.items || []).find(bot => bot.id === form.getFieldValue('bot')), [fetchedBot, bots, form.getFieldValue('bot')]);
  const livechatPlugin = useMemo(() => {
    const plugins = bot?.plugins || [];
    if (integrationType) {
      return plugins.find(plugin => plugin.integration.type === integrationType);
    }
    return plugins.find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LIVECHAT);
  }, [bot, integrationType]);
  const isHelviaLivechat = livechatPlugin?.integration?.type === INTEGRATION_TYPE.HELVIA_LIVECHAT;

  const [canSubmit, setCanSubmit] = useState(false);
  const [messagesChanged, setMessagesChanged] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);

  // State to track plugin data readiness
  const [isInitialPluginSettingsReady, setIsInitialPluginSettingsReady] = useState(false);

  useEffect(() => {
    form.resetFields();
  }, [fetchedBot?.id]);

  useEffect(() => {
    // Reset the form fields when the bot changes (except for the bot field itself)
    form.resetFields(Object.keys(form.getFieldsValue()).filter(field => field !== 'bot'));

    // Also change the language to the primary language of the bot
    if (bot) {
      changeBotLanguage(bot.settings?.languages?.primary);
    }
  }, [bot?.id]);

  // Updated form fields only once when helviaLiveChatPlugins is ready
  useEffect(() => {
    if (livechatPlugin && !isInitialPluginSettingsReady) {
      let businessHours = [];
      if (isHelviaLivechat) {
        businessHours = livechatPlugin.settings?.businessHours ? livechatPlugin.settings.businessHours.map(entry => {
          return {
            day: entry.day,
            opensAt: is.number(entry.opensAt) && moment().startOf('day').add(entry.opensAt, 'minutes'),
            closesAt: is.number(entry.closesAt) && moment().startOf('day').add(entry.closesAt, 'minutes')
          };
        }) : [];
      }
      form.setFieldsValue({
        ...livechatPlugin.settings,
        ...(isHelviaLivechat && { businessHours }),
        ...(getAgentMaskingInitialSettings(livechatPlugin?.settings?.agentNameRegex || '', livechatPlugin?.settings?.agentNameSubstitution || ''))
      });
      setIsInitialPluginSettingsReady(true);
    }

    return () => {
      setIsInitialPluginSettingsReady(false);
    };
  }, [livechatPlugin]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingBot);
    }
  }, [isUpdatingBot]);

  const getAgentMaskingInitialSettings = (agentNameRegex, agentNameSubstitution) => {

    // 1. CHECK FOR NULL/EMPTY
    // If both are null or empty strings, it means no masking is applied.
    if (!agentNameRegex && !agentNameSubstitution) {
      const fullNameMode = AGENT_MASKING_SETTINGS_MODES.find(m => m.key === 'fullName');
      return ({
        agentNameRegex: null,
        agentNameSubstitution: null,
        agentMaskingMode: fullNameMode ? fullNameMode.key : 'fullName'
      });
    }

    // 2. CHECK FOR CONSTANT NAME
    const constantMode = AGENT_MASKING_SETTINGS_MODES.find(m => m.key === 'constant');
    if (constantMode && agentNameRegex === constantMode.agentNameRegex) {
      return ({
        agentNameRegex: agentNameRegex,
        agentNameSubstitution: agentNameSubstitution,
        agentMaskingMode: constantMode.key
      });
    }

    // 3. CHECK FOR EXACT PRESET MATCHES
    const exactMatch = AGENT_MASKING_SETTINGS_MODES.find(item =>
      item.agentNameRegex === agentNameRegex
      && item.agentNameSubstitution === agentNameSubstitution
    );

    if (exactMatch) {
      return ({
        agentNameRegex: agentNameRegex,
        agentNameSubstitution: agentNameSubstitution,
        agentMaskingMode: exactMatch.key
      });
    }

    // 4. FALLBACK TO CUSTOM
    return ({
      agentNameRegex: agentNameRegex,
      agentNameSubstitution: agentNameSubstitution,
      agentMaskingMode: AGENT_MASKING_SETTINGS_CUSTOM_KEY
    });
  };

  //Handle submit state on both forms
  const updateCanSubmit = () => {
    const formHasErrors = form.getFieldsError().some((field) => field.errors.length > 0);
    const lcFormHasErrors = lcMessagesForm.getFieldsError().some((field) => field.errors.length > 0);
    const canSubmitForm = !formHasErrors && !lcFormHasErrors;
    setCanSubmit(canSubmitForm);
    if (onCanSubmitChange) {
      onCanSubmitChange(canSubmitForm);
    }
  };

  const onSubmit = async (values) => {

    /* This field is only local and is not being sent to the backend,
       it's used to identify in the client which mode we are currently in */
    if(values?.agentMaskingMode) {
      delete values.agentMaskingMode;
    }

    let isSubmitSuccessful = true;
    let isMessagesSubmitSuccessful = true;

    if (settingsChanged) {

      const { integrationId, ...valuesToSubmitRaw } = values;
      let valuesToSubmit = { ...valuesToSubmitRaw };

      if (isHelviaLivechat) {
        // Convert dates/time to desired format
        let updatedBusinessHours = [];
        if (values.businessHours) {
          for (let entry of values.businessHours) {
            updatedBusinessHours.push(
              {
                day: entry.day,
                opensAt: entry.opensAt && timeToMinutesAfterMidnight(entry.opensAt),
                closesAt: entry.closesAt && timeToMinutesAfterMidnight(entry.closesAt)
              }
            );
          }
        }
        valuesToSubmit = {
          ...valuesToSubmit,
          businessHours: updatedBusinessHours
        };
      }

      let newPlugins = [...(bot?.plugins || [])];
      const existingPluginIndex = newPlugins.findIndex(
        p => p.integration?.type === integrationType || p.integrationId === integrationId
      );

      const newPluginData = {
        integrationId: integrationId || livechatPlugin?.integration?.id,
        category: PLUGIN_CATEGORY.LIVECHAT,  // make sure to import PLUGIN_CATEGORY
        settings: valuesToSubmit
      };

      if (existingPluginIndex >= 0) {
        newPlugins[existingPluginIndex] = {
          ...newPlugins[existingPluginIndex],
          ...newPluginData
        };
      } else {
        newPlugins.push(newPluginData);
      }

      const safePlugins = newPlugins.map(p => ({
        integrationId: p.integrationId || p.integration?.id,
        category: p.category,
        settings: p.settings
      }));

      isSubmitSuccessful = await updateBot({
        orgId, tenantId: bot?.id, data: { plugins: safePlugins }
      }).unwrap();
    }

    if (messagesChanged) {
      try {
        lcMessagesForm.submit();
      } catch (error) {
        isMessagesSubmitSuccessful = false;
      }
    }

    if (isSubmitSuccessful && isMessagesSubmitSuccessful) {
      setCanSubmit(false);
      setSettingsChanged(false);
      setMessagesChanged(false);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={(changedValue, allFields) => {
          const fieldIsBot = changedValue.bot;
          if (!fieldIsBot) {
            setSettingsChanged(true);
            updateCanSubmit();
          }
          updateState(allFields);
        }}
        initialValues={{
          integrationId: livechatPlugin?.integration?.id || null,
          ...livechatPlugin?.settings,
          ...(isHelviaLivechat && { businessHours: livechatPlugin?.settings?.businessHours ? livechatPlugin?.settings?.businessHours.map(entry => {
            return {
              day: entry.day,
              opensAt: is.number(entry.opensAt) && moment().startOf('day').add(entry.opensAt, 'minutes'),
              closesAt: is.number(entry.closesAt) && moment().startOf('day').add(entry.closesAt, 'minutes')
            };
          }) : [] })
        }}
      >
        {
        // If we are in the org settings screen, we need to choose our bot
          !tenantId ? (
            <Fragment>
              <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
                <Col xs={formProps.mainColSpan}>
                  <Typography.Text>
                    {t('Select Agent')}
                  </Typography.Text>
                  <Typography.Paragraph className="fs-sm" type="secondary">
                    {t('Select an agent to configure.')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    label={t('Settings for this Agent')}
                    name="bot"
                  >
                    <Select
                      allowClear
                      className="fs-base max-field-width w-100"
                      onClear={() => {
                        form.resetFields();
                      }}
                      notFoundContent={
                        <Row className="p-4" justify="center" align="middle">
                          {
                            isFetchingBots ? (
                              <Spin />
                            ) : (
                              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No agents found')} />
                            )
                          }
                        </Row>
                      }
                      placeholder={t('Select an agent')}
                      onSelect={(id) => {
                        const bot = bots.items.find(bot => bot.id === id);
                        if (!bot?.settings?.liveChat) {
                          return form.setFieldsValue({
                            enabled: null,
                            version: null,
                            expirationThreshold: null,
                            businessHours: [],
                            averageAgentResponseTime: null,
                            averageWaitingTimePerUser: null
                          });
                        }
                        const { enabled, version, expirationThreshold, businessHours, averageAgentResponseTime, averageWaitingTimePerUser } = bot.settings.liveChat;

                        form.setFieldsValue({
                          enabled,
                          version,
                          expirationThreshold,
                          averageAgentResponseTime,
                          averageWaitingTimePerUser,
                          businessHours: (businessHours ?? []).map(slot => (
                            {
                              day: slot.day,
                              opensAt: is.number(slot.opensAt) && moment().startOf('day').add(slot.opensAt, 'minutes'),
                              closesAt: is.number(slot.closesAt) && moment().startOf('day').add(slot.closesAt, 'minutes')
                            }
                          ))
                        });
                      }}
                      showSearch
                      size="large"
                      filterOption={(input, option) =>
                        bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {
                        (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.integration.type === INTEGRATION_TYPE.HELVIA_LIVECHAT)).map(bot => (
                          <Select.Option key={bot.id} value={bot.id}>
                            <BotAvatar bot={bot} avatarSize={28} />
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />
            </Fragment>
          ) : null
        }

        {/* LiveChat availability section */}
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          {showIntegrationSelect && (
            <Fragment>
              <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
                <Col xs={formProps.mainColSpan}>
                  <Typography.Text>{t('Integration Configuration')}</Typography.Text>
                  <Typography.Paragraph className="fs-sm" type="secondary">
                    {t('Select which available integration this agent plugin will use. Changing the integration does not cause downtime for the plugin.')}
                  </Typography.Paragraph>
                </Col>
                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    name="integrationId"
                    label={t('Select Integration')}
                    rules={[{ required: true, message: t('Please select an integration.') }]}
                  >
                    <Select
                      placeholder={t('Select Integration')}
                      loading={isFetchingIntegrations}
                      options={integrationOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider className="my-4" />
            </Fragment>
          )}
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>
              {t('LiveChat Availability')}
            </Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Switch availability on or off.')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="enabled"
              valuePropName="checked"
              label={t('LiveChat Availability')}
              colon={false}
            >
              <Switch
                disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                defaultChecked={bot?.settings?.liveChat?.enabled ?? false}
                loading={isFetchingBot || isUpdatingBot}
              />
            </Form.Item>
          </Col>
        </Row>

        {
          enableAgentMaskingSettings
          && <Divider />
        }

        <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mt-7`} hidden={!enableAgentMaskingSettings}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>
              {t('Agent Masking')}
            </Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t('Control how agent names appear in LiveChat sessions.')}
            </Typography.Paragraph>
          </Col>
          <Col xs={formProps.secondaryColSpan}>
            <AgentMasking form={form} />
          </Col>
        </Row>

        {isHelviaLivechat ? (
          <Fragment>
            <Divider />
            {/* LiveChat request timeout section */}
            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text>
                  {t('LiveChat Request Timeout')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Set pending expiration time (seconds)')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  label={<Typography.Text>
                    {t('Request Timeout (seconds)')}
                    <Tooltip
                      className="ml-1"
                      placement="top"
                      title={t('Set pending expiration time in seconds.')}
                    >
                      <QuestionCircleOutlined className="fs-sm position-absolute" />
                    </Tooltip>
                  </Typography.Text>}
                  name="expirationThreshold"
                  rules={[{ required: true, message: `${t('Please enter a value')}.` }]}
                >
                  <CustomInputNumber
                    disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                    min={1}
                    size="large"
                    step={30}
                    placeholder={'-'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider/>

            {/* Business hours section */}
            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text>
                  {t('Business Hours')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Configure the LiveChat operating hours.The agent will forward LiveChat requests only during the specified time slots.')}
                </Typography.Paragraph>

                {
                  bot ? (
                    <Typography.Paragraph className="fs-sm" type="secondary">
                      {t('Timezone')}: {bot.timezone}
                    </Typography.Paragraph>
                  ) : null
                }
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Row gutter={16}>
                  <Col sm={24}>
                    <Form.List
                      name="businessHours"
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <Fragment>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <Row gutter={16} key={index}>
                              <Col flex="auto">
                                <Row gutter={16}>
                                  <Col sm={12} xs={24}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'day']}
                                      label={index === 0 ? t('Day of Week') : ''}
                                      validateTrigger={['onChange', 'onBlur']}
                                      rules={[{
                                        required: true,
                                        message: t('Please select a day or delete this field')
                                      }]}
                                    >
                                      <Select
                                        placeholder={t('Select weekday')}
                                        showSearch
                                        optionFilterProp="children"
                                        notFoundContent={t('Not Found')}
                                        size="large"
                                        disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                                        getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                                      >
                                        {
                                          Object.keys(WEEKDAYS).map(key =>
                                            <Option key={WEEKDAYS[key]} value={WEEKDAYS[key]}>{key}</Option>
                                          )
                                        }
                                      </Select>
                                    </Form.Item>
                                  </Col>

                                  <Col sm={6} xs={10}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'opensAt']}
                                      label={index === 0 ? t('Start Time') : ''}
                                      validateTrigger={['onChange', 'onBlur']}
                                      trigger="onSelect"
                                      rules={[{
                                        required: true,
                                        message: t('Please pick a time')
                                      }, {
                                        validator: async (_rule, value) => {
                                        // Make sure start time is less than end time
                                          if (value && value >= form.getFieldValue(['businessHours', index, 'closesAt'])) {
                                            return Promise.reject(new Error(t('Choose an earlier time')));
                                          }

                                          // Else
                                          return Promise.resolve();
                                        }
                                      }]}
                                    >
                                      <TimePicker
                                        className="w-100"
                                        placeholder={t('Opens at')}
                                        format="HH:mm"
                                        size="large"
                                        minuteStep={5}
                                        disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                                        onSelect={(value) => {
                                          // Save the value without having to press Enter or click on OK
                                          form.setFieldsValue({ businessHours: form.getFieldValue('businessHours').map((entry, entryIndex) => {
                                            if (entryIndex === index) {
                                              return {
                                                ...entry,
                                                opensAt: value
                                              };
                                            } else {
                                              return entry;
                                            }
                                          }) });
                                          if (form.getFieldError(['businessHours', index, 'closesAt']).length > 0) {
                                            form.validateFields([['businessHours', index, 'closesAt']]);
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col sm={6} xs={10}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'closesAt']}
                                      label={index === 0 ? t('End Time') : ''}
                                      validateTrigger={['onChange', 'onBlur']}
                                      trigger="onSelect"
                                      rules={[{
                                        required: true,
                                        message: t('Please pick a time')
                                      }, {
                                        validator: async (_rule, value) => {
                                          // Make sure start time is less than end time
                                          if (value && value <= form.getFieldValue(['businessHours', index, 'opensAt'])) {
                                            return Promise.reject(new Error(t('Choose a later time')));
                                          }
                                          return Promise.resolve();
                                        }
                                      }]}
                                    >
                                      <TimePicker
                                        className="w-100"
                                        placeholder={t('Closes at')}
                                        format="HH:mm"
                                        size="large"
                                        minuteStep={5}
                                        disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                                        onSelect={(value) => {
                                          // Save the value without having to press Enter or click on OK
                                          form.setFieldsValue({ businessHours: form.getFieldValue('businessHours').map((entry, entryIndex) => {
                                            if (entryIndex === index) {
                                              return {
                                                ...entry,
                                                closesAt: value
                                              };
                                            } else {
                                              return entry;
                                            }
                                          }) });
                                          if (form.getFieldError(['businessHours', index, 'opensAt']).length > 0) {
                                            form.validateFields([['businessHours', index, 'opensAt']]);
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Col>

                              <Col flex="none">
                                <Button
                                  size="large"
                                  type="text"
                                  className={classNames('bordered border-radius-base', { 'mt-7': index === 0 })}
                                  icon={<DeleteIcon />}
                                  onClick={() => remove(name)}
                                  disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                                />
                              </Col>
                            </Row>
                          ))}

                          <Row gutter={16}>
                            <Col sm={8} xs={24}>
                              <Button
                                type="dashed"
                                size="large"
                                block
                                onClick={() => add({ day: 1, opensAt: moment('09:00', 'HH:mm'), closesAt: moment('17:00', 'HH:mm') })}
                                icon={<PlusIcon />}
                                disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                                className="max-field-width w-100"
                              >
                                {t('Add New Slot')}
                              </Button>
                            </Col>
                          </Row>

                          <Form.ErrorList errors={errors} />
                        </Fragment>
                      )}
                    </Form.List>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Divider />

            {/* LiveChat request timeout section */}
            <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
              <Col xs={formProps.mainColSpan}>
                <Typography.Text>
                  {t('LiveChat Queue Configuration')}
                </Typography.Text>
                <Typography.Paragraph className="fs-sm" type="secondary">
                  {t('Set the Average LiveChat Agent Response Time and the Average End-User Waiting Time to provide waiting time information to the End-User, depending on the number of users waiting in queue.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  label={t('Average LiveChat Agent Response Time (seconds)')}
                  name="averageAgentResponseTime"
                >
                  <CustomInputNumber
                    disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                    min={0}
                    size="large"
                    step={30}
                    placeholder={'-'}
                  />
                </Form.Item>

                <Form.Item
                  label={t('Average End-User Waiting Time (seconds)')}
                  name="averageWaitingTimePerUser"
                >
                  <CustomInputNumber
                    disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission)) || !bot}
                    min={0}
                    size="large"
                    step={30}
                    placeholder={'-'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        ) : null}
      </Form>

      <Divider />

      {/* LiveChat system messages section */}
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('LiveChat System Messages')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Customize the system messages that are sent to the end-user during the LiveChat conversation.')}
          </Typography.Paragraph>

          <BotLanguageSelect
            tenantId={tenantId || bot?.id}
            className="w-100"
            getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
            disabled={!bot}
            hideButtonIcon
          />
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <LivechatSystemMessagesEditor
            form={lcMessagesForm}
            tenantId={tenantId || bot?.id}
            onFieldChange={() => {
              setMessagesChanged(true);
              updateCanSubmit();
            }}
            onLoadingChange={onLoadingChange} //propagate to parent
          />
        </Col>
      </Row>

      {/* Prompt to save changes */}
      <Prompt
        when={canSubmit}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      {!onSuccess ? (
        <Row justify="end" className="mt-4">
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              disabled={!canSubmit}
              loading={isUpdatingBot}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      ) : null}
    </Fragment>
  );
};

LiveChatBotSettingsForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCanSubmitChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  enableAgentMaskingSettings: PropTypes.bool,
  integrationType: PropTypes.string,
  showIntegrationSelect: PropTypes.bool
};

export default LiveChatBotSettingsForm;
